import { generateMeta } from '@forge42/seo-tools/remix/metadata'

// Canonical production origin. Kept here so canonical + absolute OG image URLs
// have a single source of truth. (competitorbacklinks.com runs as a focused
// property; if it later sits under a parent brand, only the Organization
// JSON-LD below needs to change — not every page.)
export const SITE_URL = 'https://competitorbacklinks.com'

// Site-wide default social share image (generated by other/og/generate-og.mjs).
export const DEFAULT_OG_IMAGE = '/img/og/og-default.png'

const SITE_NAME = 'Competitor Backlinks'

// Parent brand. Competitor Backlinks is a property of DFW AI; these entities are
// referenced from Organization, Article publisher, and the author's employer so
// Google can connect this site to the DFWAI entity graph it already knows.
const PARENT_ORG_ID = 'https://dfwai.com/#organization'
const PARENT_ORG = {
	'@type': 'Organization',
	'@id': PARENT_ORG_ID,
	name: 'DFW AI',
	url: 'https://dfwai.com',
	sameAs: [
		'https://dfwai.com/about',
		'https://www.youtube.com/@dfwai_com',
		'https://x.com/DFWStrategy',
		'https://www.facebook.com/dfwailabs/',
	],
}

// Canonical author entity. Brad Williams is a real operator with an established
// presence (DFWAI site + YouTube + socials); `sameAs` ties every byline to that
// entity for E-E-A-T. Single source of truth — referenced by every Article.
export const AUTHOR_ID = `${SITE_URL}/#brad-williams`
export const AUTHOR_NAME = 'Brad Williams'
function authorEntity() {
	return {
		'@type': 'Person',
		'@id': AUTHOR_ID,
		name: AUTHOR_NAME,
		url: `${SITE_URL}/authors/brad-williams`,
		sameAs: PARENT_ORG.sameAs,
		worksFor: { '@id': PARENT_ORG_ID },
	}
}

/** Full Person JSON-LD for the author bio page (same @id as every byline). */
export function authorPersonJsonLd() {
	return { '@context': 'https://schema.org', ...authorEntity() }
}

// Publisher entity for Article JSON-LD (Google's Article guidelines want a
// publisher with a logo). Same @id as the homepage Organization.
function publisherEntity() {
	return {
		'@type': 'Organization',
		'@id': `${SITE_URL}/#organization`,
		name: SITE_NAME,
		logo: { '@type': 'ImageObject', url: abs('/img/cb-link.png') },
	}
}

/** Absolute-ize a root-relative path against the canonical origin. */
function abs(path: string) {
	return path.startsWith('http') ? path : `${SITE_URL}${path}`
}

/**
 * The complete <head> meta for a marketing page: everything generateMeta emits
 * (title, og:title/description/url, twitter:card) PLUS the tags it omits and
 * every page needs — canonical, og:image + twitter:image (absolute, so social
 * scrapers resolve them), og:type, and og:site_name.
 *
 * `path` is the route's path (e.g. "/guides/find-competitor-backlinks"); it
 * drives both canonical and og:url so they can never drift.
 */
export function buildMeta({
	title,
	description,
	path,
	image = DEFAULT_OG_IMAGE,
	type = 'website',
}: {
	title: string
	description: string
	path: string
	image?: string
	type?: 'website' | 'article'
}) {
	const url = abs(path)
	const imageUrl = abs(image)
	return [
		...generateMeta({ title, description, url }, []),
		{ tagName: 'link', rel: 'canonical', href: url },
		{ property: 'og:type', content: type },
		{ property: 'og:site_name', content: SITE_NAME },
		{ property: 'og:image', content: imageUrl },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ name: 'twitter:image', content: imageUrl },
	]
}

/**
 * The `author` + `publisher` fields every Article JSON-LD should carry. Spread
 * into the article object so all content shares one author/publisher entity.
 */
export function articleAttribution(author?: string) {
	return {
		// Only emit a Person author when the byline matches our canonical author;
		// otherwise fall back to a plain name so schema stays truthful.
		...(author
			? {
					author:
						author === AUTHOR_NAME
							? authorEntity()
							: { '@type': 'Person', name: author },
				}
			: {}),
		publisher: publisherEntity(),
	}
}

/**
 * FAQPage JSON-LD from the same Q&A pairs the page renders. Pass the exact
 * strings shown on the page so the rich result never drifts from the content
 * (Google's FAQ guidelines require the markup to match visible text).
 */
export function faqPageJsonLd(faqs: ReadonlyArray<{ q: string; a: string }>) {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs.map(({ q, a }) => ({
			'@type': 'Question',
			name: q,
			acceptedAnswer: { '@type': 'Answer', text: a },
		})),
	}
}

/**
 * Organization + WebSite JSON-LD for the homepage. Competitor Backlinks is a
 * property of DFW AI — linked via `parentOrganization` + `sameAs`.
 */
export function siteJsonLd() {
	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'Organization',
				'@id': `${SITE_URL}/#organization`,
				name: SITE_NAME,
				url: SITE_URL,
				logo: abs('/img/cb-link.png'),
				parentOrganization: { '@id': PARENT_ORG_ID },
				sameAs: PARENT_ORG.sameAs,
			},
			PARENT_ORG,
			{
				'@type': 'WebSite',
				'@id': `${SITE_URL}/#website`,
				name: SITE_NAME,
				url: SITE_URL,
				publisher: { '@id': `${SITE_URL}/#organization` },
			},
		],
	}
}

/**
 * CollectionPage + BreadcrumbList + ItemList JSON-LD for a listing page.
 *
 * The `/guides` and `/best-tools` indexes are where the hub-and-spoke structure
 * is actually expressed, but they carried no structured data at all, so the
 * relationship between a hub and its articles was visible only to a human
 * reading the page. ItemList states the membership explicitly, and the
 * BreadcrumbList gives the section the same trail its articles already emit.
 */
export function collectionJsonLd({
	title,
	name,
	description,
	path,
	items,
}: {
	/** The full SEO title, brand suffix and all. */
	title: string
	/**
	 * Short section label for the breadcrumb crumb, e.g. "Guides". Without this
	 * the crumb inherits the meta title and renders as "Backlink Guides |
	 * Competitor Backlinks", which is a page title, not a breadcrumb.
	 */
	name: string
	description: string
	path: string
	items: ReadonlyArray<{ title: string; path: string }>
}) {
	const url = abs(path)
	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'CollectionPage',
				'@id': url,
				url,
				name,
				description,
				isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
				publisher: publisherEntity(),
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: [
					{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
					{ '@type': 'ListItem', position: 2, name, item: url },
				],
			},
			{
				'@type': 'ItemList',
				itemListElement: items.map((item, i) => ({
					'@type': 'ListItem',
					position: i + 1,
					name: item.title,
					url: abs(item.path),
				})),
			},
		],
	}
}

/**
 * WebApplication + BreadcrumbList JSON-LD for the free tool pages.
 *
 * The checker pages were carrying only FAQPage, which describes the copy around
 * the tool rather than the tool itself. WebApplication is the type Google reads
 * for software, and the free `offers` node is the part that matters: it states
 * in machine-readable terms that the thing costs nothing, which is the single
 * strongest differentiator these pages have against $129/mo suites.
 *
 * Deliberately no `aggregateRating`. We have no collected reviews, and inventing
 * star ratings to win a rich snippet is exactly the manipulation Google strips
 * sites for. Add it only when real reviews exist to point at.
 */
export function webAppJsonLd({
	name,
	description,
	path,
	image = DEFAULT_OG_IMAGE,
	breadcrumb,
}: {
	name: string
	description: string
	path: string
	image?: string
	/** Short crumb label, e.g. "Free backlink checker". */
	breadcrumb: string
}) {
	const url = abs(path)
	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebApplication',
				'@id': `${url}#app`,
				url,
				name,
				description,
				image: abs(image),
				applicationCategory: 'BusinessApplication',
				applicationSubCategory: 'SEO',
				operatingSystem: 'Any',
				browserRequirements: 'Requires JavaScript',
				isAccessibleForFree: true,
				offers: {
					'@type': 'Offer',
					price: '0',
					priceCurrency: 'USD',
					availability: 'https://schema.org/InStock',
				},
				publisher: publisherEntity(),
				isPartOf: { '@type': 'WebSite', '@id': `${SITE_URL}/#website` },
			},
			{
				'@type': 'BreadcrumbList',
				itemListElement: [
					{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
					{ '@type': 'ListItem', position: 2, name: breadcrumb, item: url },
				],
			},
		],
	}
}
